import React from 'react';
import { useTranslation } from 'localization';
import { ApproachTreeNode, ApproachTreeNodeData, ProvidedTypeNodeData, ProvidedContextNodeData } from 'types/approachExplorer';
import { SchemaProperty } from 'types/schema';
import ContextSummaryMolecule from '../ContextSummary/ContextSummaryMolecule';
import './ApproachNodeDetailMolecule.scss';

export interface ApproachNodeDetailMoleculeProps {
  /** The currently selected tree node to display details for. */
  node?: ApproachTreeNode | null;
  /** Additional CSS class name. */
  className?: string;
}

const KEY = 'marketplace.approachExplorer.detail';

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="approach-node-detail__row">
      <span className="approach-node-detail__label">{label}</span>
      <span className="approach-node-detail__value">{value}</span>
    </div>
  );
};

const Badge: React.FC<{ label: string; variant?: 'default' | 'success' | 'warning' }> = ({
  label,
  variant = 'default',
}) => <span className={`approach-node-detail__badge approach-node-detail__badge--${variant}`}>{label}</span>;

const PROPERTY_TYPE_ICONS: Record<string, string> = {
  string: 'pi-align-left',
  number: 'pi-hashtag',
  boolean: 'pi-check-square',
  date: 'pi-calendar',
  uuid: 'pi-id-card',
  enum: 'pi-list',
  json: 'pi-code',
};

function renderProvidedTypeDetail(
  data: ProvidedTypeNodeData,
  t: (key: string, opts?: Record<string, unknown>) => string
): React.ReactNode {
  const visibleProps = data.properties?.filter((p: SchemaProperty) => !p.hidden) ?? [];

  return (
    <>
      {data.avatar && (
        <div className="approach-node-detail__type-avatar">
          <i className={data.avatar} />
        </div>
      )}
      <DetailRow label={t(`${KEY}.displayName`)} value={data.displayName} />
      {data.description && <DetailRow label={t(`${KEY}.description`)} value={data.description} />}
      <DetailRow label={t(`${KEY}.schemaSystemName`)} value={data.schemaSystemName} />
      {data.isForked && <Badge label={t('marketplace.approachDetail.providedTypes.forked')} variant="warning" />}

      {visibleProps.length > 0 && (
        <div className="approach-node-detail__section">
          <h4 className="approach-node-detail__section-title">{t(`${KEY}.properties`)}</h4>
          <div className="approach-node-detail__property-list">
            {visibleProps.map((prop: SchemaProperty, idx: number) => {
              const iconClass = PROPERTY_TYPE_ICONS[prop.type] || 'pi-code';
              return (
                <div key={prop.name ?? idx} className="approach-node-detail__property-row">
                  <i className={`pi ${iconClass} approach-node-detail__property-icon`} aria-label={prop.type} />
                  <div className="approach-node-detail__property-info">
                    <span className="approach-node-detail__property-name">{prop.label || prop.name}</span>
                    {prop.description && (
                      <span className="approach-node-detail__property-desc">{prop.description}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.perspectives && data.perspectives.length > 0 && (
        <div className="approach-node-detail__section">
          <h4 className="approach-node-detail__section-title">{t(`${KEY}.perspectives`)}</h4>
          <div className="approach-node-detail__chip-list">
            {data.perspectives.map((p) => (
              <Badge key={p.system_name} label={p.label || p.name} />
            ))}
          </div>
        </div>
      )}

      {data.useCases && data.useCases.length > 0 && (
        <div className="approach-node-detail__section">
          <h4 className="approach-node-detail__section-title">{t(`${KEY}.useCases`)}</h4>
          <div className="approach-node-detail__chip-list">
            {data.useCases.map((uc) => (
              <Badge key={uc} label={uc} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function renderProvidedContextDetail(
  data: ProvidedContextNodeData,
  t: (key: string, opts?: Record<string, unknown>) => string
): React.ReactNode {
  return (
    <>
      <DetailRow label={t(`${KEY}.displayName`)} value={data.displayName} />
      {data.description && <DetailRow label={t(`${KEY}.description`)} value={data.description} />}
      <DetailRow label={t(`${KEY}.contextSystemName`)} value={data.contextSystemName} />
      {data.contextDefinition && (
        <div className="approach-node-detail__section">
          <ContextSummaryMolecule
            context={{
              systemName: data.contextSystemName,
              name: data.displayName,
              description: data.description,
              definition: data.contextDefinition,
              applicability: data.applicability,
              useCaseTags: data.useCaseTags,
            }}
            mode="toggleable"
            data-testid="context-summary"
          />
        </div>
      )}
    </>
  );
}

function renderNodeDetail(data: ApproachTreeNodeData, t: (key: string, opts?: Record<string, unknown>) => string): React.ReactNode {
  switch (data.type) {
    case 'approach':
      return (
        <>
          <DetailRow label={t(`${KEY}.systemName`)} value={data.systemName} />
          {data.definition.deploymentMode && (
            <DetailRow
              label={t(`${KEY}.deploymentMode`)}
              value={t(`marketplace.approachDetail.deploymentMode.${data.definition.deploymentMode}`)}
            />
          )}
          {data.version != null && <DetailRow label={t(`${KEY}.version`)} value={`v${data.version}`} />}
          {data.mode && (
            <DetailRow
              label={t(`${KEY}.mode`)}
              value={t(`marketplace.approachDetail.subApproaches.modes.${data.mode === 'include' ? 'included' : 'dependency'}`)}
            />
          )}
        </>
      );

    case 'provided-type':
      return renderProvidedTypeDetail(data, t);

    case 'provided-context':
      return renderProvidedContextDetail(data, t);

    case 'arc-stage': {
      const { stage } = data;
      return (
        <>
          <DetailRow label={t(`${KEY}.stageName`)} value={stage.fallbackName} />
          <DetailRow
            label={t(`${KEY}.stageType`)}
            value={t(`marketplace.approachDetail.arc.stageTypes.${stage.type}`)}
          />
          <DetailRow
            label={t(`${KEY}.transitionType`)}
            value={t(`marketplace.approachDetail.arc.transitionTypes.${stage.transition.type}`)}
          />
          {stage.intents && stage.intents.length > 0 && (
            <DetailRow
              label={t(`${KEY}.intents`)}
              value={t('marketplace.approachDetail.arc.intentCount', { count: stage.intents.length })}
            />
          )}
          {stage.isTerminal && <Badge label={t('marketplace.approachDetail.arc.terminal')} />}
          {data.arcName && <DetailRow label={t(`${KEY}.arcName`)} value={data.arcName} />}
        </>
      );
    }

    case 'child-template': {
      const { child } = data;
      return (
        <>
          <DetailRow label={t(`${KEY}.childName`)} value={child.name?.default} />
          <DetailRow label={t(`${KEY}.schemaType`)} value={child.schemaSystemName} />
          <DetailRow
            label={t(`${KEY}.creationMode`)}
            value={t(`marketplace.approachDetail.children.creationModes.${child.creationMode === 'auto' ? 'autoCreated' : 'recommended'}`)}
          />
          {child.condition && <DetailRow label={t(`${KEY}.condition`)} value={t(`${KEY}.hasCondition`)} />}
        </>
      );
    }

    case 'status-factor': {
      const { factor } = data;
      const typeKey = (() => {
        if (factor.factorType === 'manual') return 'manualAssessment';
        if (factor.factorType === 'arc_health') return 'arcHealth';
        if (factor.factorType === 'related_spheres') return 'relatedSpheres';
        return 'custom';
      })();
      return (
        <>
          <DetailRow
            label={t(`${KEY}.factorType`)}
            value={t(`marketplace.approachDetail.statusFactors.factorTypes.${typeKey}`)}
          />
          <DetailRow
            label={t(`${KEY}.weight`)}
            value={t('marketplace.approachDetail.statusFactors.weight', { percent: Math.round(factor.weight * 100) })}
          />
          <Badge
            label={factor.enabled
              ? t('marketplace.approachDetail.statusFactors.enabled')
              : t('marketplace.approachDetail.statusFactors.disabled')}
            variant={factor.enabled ? 'success' : 'warning'}
          />
        </>
      );
    }

    case 'orbit-schedule': {
      const { schedule } = data;
      return (
        <>
          <DetailRow
            label={t(`${KEY}.scheduleType`)}
            value={t(`marketplace.approachDetail.orbit.scheduleTypes.${schedule.type}`)}
          />
          {data.orbitSource && <DetailRow label={t(`${KEY}.source`)} value={data.orbitSource} />}
        </>
      );
    }

    case 'deployment-input': {
      const { input } = data;
      const inputTypeKey = (() => {
        if (input.inputType === 'date_range') return 'dateRange';
        if (input.inputType === 'person_select') return 'personSelect';
        if (input.inputType === 'sphere_select') return 'sphereSelect';
        return input.inputType;
      })();
      return (
        <>
          {input.description && <DetailRow label={t(`${KEY}.description`)} value={input.description.default} />}
          <DetailRow
            label={t(`${KEY}.inputType`)}
            value={t(`marketplace.approachDetail.deploymentInputs.inputTypes.${inputTypeKey}`)}
          />
          <Badge
            label={input.required
              ? t('marketplace.approachDetail.deploymentInputs.required')
              : t('marketplace.approachDetail.deploymentInputs.optional')}
            variant={input.required ? 'warning' : 'default'}
          />
          {input.default != null && <DetailRow label={t(`${KEY}.defaultValue`)} value={String(input.default)} />}
        </>
      );
    }

    case 'sub-approach-ref': {
      const { reference } = data;
      return (
        <>
          <DetailRow label={t(`${KEY}.systemName`)} value={reference.approachSystemName} />
          <DetailRow
            label={t(`${KEY}.mode`)}
            value={t(`marketplace.approachDetail.subApproaches.modes.${reference.mode === 'include' ? 'included' : 'dependency'}`)}
          />
          <p className="approach-node-detail__hint">{t(`${KEY}.expandPrompt`)}</p>
        </>
      );
    }

    default:
      return null;
  }
}

const ApproachNodeDetailMolecule: React.FC<ApproachNodeDetailMoleculeProps> = ({ node, className }) => {
  const { t } = useTranslation();
  const classes = ['approach-node-detail', className].filter(Boolean).join(' ');

  if (!node) {
    return (
      <div className={classes} data-testid="approach-node-detail">
        <div className="approach-node-detail__empty">
          <i className="pi pi-info-circle approach-node-detail__empty-icon" />
          <p>{t(`${KEY}.emptyState`)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classes} data-testid="approach-node-detail">
      <div className="approach-node-detail__header">
        {node.icon && <i className={`approach-node-detail__header-icon pi ${node.icon}`} />}
        <h3 className="approach-node-detail__title">{node.label}</h3>
      </div>
      {node.description && node.type !== 'category' && (
        <p className="approach-node-detail__description">{node.description}</p>
      )}
      {node.data && (
        <div className="approach-node-detail__fields">
          {renderNodeDetail(node.data, t)}
        </div>
      )}
      {node.type === 'category' && (
        <p className="approach-node-detail__category-text">{node.description || t(`${KEY}.categoryInfo`)}</p>
      )}
    </div>
  );
};

ApproachNodeDetailMolecule.displayName = 'ApproachNodeDetailMolecule';

export default ApproachNodeDetailMolecule;
